import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../../database/firebase';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ExternalLink, Calendar, Users, Briefcase } from 'lucide-react';
import CompanyProfile from './CompanyProfile';
import { toast } from 'react-toastify';

export default function TeamMemberDashboard() {
  const [teamData, setTeamData] = useState(null);
  const [organizationData, setOrganizationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const loadTeamData = async () => {
      try {
        const storedData = localStorage.getItem('teamMemberData');
        if (!storedData) {
          navigate('/team-login');
          return;
        }

        const data = JSON.parse(storedData);
        setTeamData(data);

        const orgDoc = await getDoc(doc(firestore, 'organizations', data.organizationId));
        if (orgDoc.exists()) {
          setOrganizationData(orgDoc.data());
        }
      } catch (error) {
        toast.error('Error loading team data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('teamMemberData');
    navigate('/team-login');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="h-12 w-12 rounded-full border-4 border-slate-300 border-t-slate-600"
        />
      </div>
    );
  }

  if (!teamData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="text-center">
          <p className="text-red-500">Access denied. Please log in again.</p>
          <button
            onClick={() => navigate('/team-login')}
            className="mt-4 rounded-xl bg-slate-900 px-4 py-2 text-white"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="border-b bg-white shadow-sm"
      >
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <Building2 className="h-8 w-8 text-slate-600" />
                <div>
                  <h1 className="text-xl font-semibold text-slate-900">
                    {organizationData?.organization || 'Organization Dashboard'}
                  </h1>
                  <p className="text-sm text-slate-600">Team Member Access</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{teamData.email}</p>
                <p className="text-xs text-slate-600">Team Member</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-slate-600 transition-colors hover:text-slate-900"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 md:py-6">
        <div className="flex space-x-1 rounded-xl bg-white p-1 shadow-sm">
          {[
            { id: 'overview', label: 'Overview', icon: Building2 },
            { id: 'profile', label: 'Company Profile', icon: ExternalLink },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 rounded-lg px-4 py-2 transition-all ${
                  activeTab === tab.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 md:pb-12">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-xl font-semibold text-slate-900">
                Organization Information
              </h2>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Organization Name</label>
                    <p className="text-lg text-slate-900">
                      {organizationData?.organization || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Owner Email</label>
                    <p className="text-lg text-slate-900">{teamData.ownerEmail}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Your Email</label>
                    <p className="text-lg text-slate-900">{teamData.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600">Member Since</label>
                    <p className="text-lg text-slate-900">
                      {teamData.createdAt
                        ? new Date(teamData.createdAt.seconds * 1000).toLocaleDateString()
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="rounded-2xl border bg-white p-6 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-blue-100 p-2">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Team Members</p>
                    <p className="text-2xl font-semibold text-slate-900">
                      {organizationData?.members?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-6 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-green-100 p-2">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Access Level</p>
                    <p className="text-2xl font-semibold text-slate-900">Team Member</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border bg-white p-6 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-purple-100 p-2">
                    <Briefcase className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Status</p>
                    <p className="text-2xl font-semibold capitalize text-slate-900">
                      {teamData.status || 'Active'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'profile' && <CompanyProfile organizationData={organizationData} />}
      </div>
    </div>
  );
}
